import type { Asset } from '../../../../api/assetAdministrationBackendCodec.ts';
import { useState } from 'react';
import { AssetArchivingDialog } from '../../dialogs/AssetArchivingDialog.tsx';
import { archiveAsset } from '../../../../api/api.ts';
import Notification from '@rio-cloud/rio-uikit/Notification';
import { sleep } from '../../../../../utils.ts';
import { useAppDispatch } from '../../../../../configuration/setup/hooks.ts';
import { useIntl } from 'react-intl';
import { assetModified } from '../assetSidebarSlice.ts';
import type { ContractDialogState } from '../../dialogs/contractDialogUtils.ts';
import { ContractDialog } from '../../dialogs/ContractDialog.tsx';
import { getAccountsWithContracts } from '../../../../api/contracts/getAccountsWithContracts.ts';

export const ArchiveAsset = ({ asset }: { asset: Asset }) => {
    const [showDialog, setShowDialog] = useState(false);
    const dispatch = useAppDispatch();
    const intl = useIntl();

    const miniAsset = [
        {
            id: asset.id,
            vin: asset.identification,
            accountId: asset.account_id,
            status: asset.status,
        },
    ];

    const [contractDialogState, setContractDialogState] = useState<ContractDialogState>({
        showDialog: false,
        requestState: undefined,
        accountsWithContracts: [],
        errorMessage: undefined,
        checkBoxChecked: false,
        intent: 'archive',
    });
    const updateContractDialogState = (newState: Partial<ContractDialogState>) => {
        setContractDialogState(prevState => ({ ...prevState, ...newState }));
    };

    const initiateArchiveAssets = async () => {
        updateContractDialogState({ requestState: 'loading', showDialog: true });
        try {
            const accountsWithContracts = await getAccountsWithContracts(miniAsset);
            if (accountsWithContracts.length > 0) {
                updateContractDialogState({
                    requestState: 'resolved',
                    accountsWithContracts: accountsWithContracts,
                });
            } else {
                updateContractDialogState({ showDialog: false });
                setShowDialog(true);
            }
        } catch (error) {
            console.error(`Could not initiate archive assets: ${error}`);
            updateContractDialogState({ requestState: 'error' });
        }
    };

    const handleArchiveConfirm = (assetIds: string[], reason: string) => {
        archiveAsset(assetIds[0], reason)
            .then(() => sleep(1000))
            .then(() => dispatch(assetModified()))
            .then(() => Notification.success('Asset archived'))
            .catch(error => {
                console.error(`Could not archive asset: ${error}`);
                Notification.error(intl.formatMessage({ id: 'assets-helpgang.notification.couldNotArchiveAsset.one' }));
            });
        setShowDialog(false);
    };

    return (
        <>
            <button
                type={'button'}
                className={'btn btn-info'}
                onClick={initiateArchiveAssets}
                data-cy='archive-asset-button'
            >
                <span className={'rioglyph rioglyph rioglyph-inbox-in'} />
                Archive
            </button>
            <ContractDialog
                state={contractDialogState}
                updateState={updateContractDialogState}
                handleConfirm={() => {
                    setShowDialog(true);
                }}
                handleRefresh={initiateArchiveAssets}
            />
            <AssetArchivingDialog
                showArchivingDialog={showDialog}
                setShowArchivingDialog={setShowDialog}
                assets={miniAsset}
                handleArchiveConfirm={handleArchiveConfirm}
            />
        </>
    );
};
